// PROMPT 149: notas internas del equipo. El cliente nunca las ve y no salen por WhatsApp.
import { useEffect, useRef, useState } from "react";
import { StickyNote, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_NOTA = 4096;

function horaDe(valor) {
  try {
    const d = new Date(valor);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

// El boton de la zona de escribir. Esta siempre, tambien con "Elegir plantilla" o "Desarchivar".
export function BotonNota({ onClick, deshabilitado, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={deshabilitado}
      title="Nota para el equipo (el cliente no la ve)"
      aria-label="Nota para el equipo"
      data-testid="wa-nota"
      className={`inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[13px] font-medium text-slate-600 hover:bg-black/5 hover:text-slate-800 disabled:opacity-40 ${className}`}
    >
      <StickyNote className="h-5 w-5" />
      <span className="hidden sm:inline">Nota</span>
    </button>
  );
}

// La zona de escribir en amarillo: lo que se escribe aca es solo para el equipo.
export function NotaDelEquipo({ conversacionId, onGuardada, onCancelar }) {
  const [texto, setTexto] = useState("");
  const [guardando, setGuardando] = useState(false);
  const area = useRef(null);
  const pasadoDeLargo = texto.length > MAX_NOTA;

  useEffect(() => {
    if (area.current) area.current.focus();
  }, []);

  useEffect(() => {
    const ta = area.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 128)}px`;
  }, [texto]);

  const guardar = async () => {
    const limpio = texto.trim();
    if (!limpio || guardando || pasadoDeLargo) return;
    setGuardando(true);
    try {
      const { data } = await api.post(`/whatsapp/conversaciones/${encodeURIComponent(conversacionId)}/nota`, {
        texto: limpio,
      });
      if (onGuardada) onGuardada(data && data.mensaje);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="relative flex items-end gap-2 bg-[#fff3b0] px-3 py-2.5" data-testid="wa-nota-zona">
      <div className="min-w-0 flex-1">
        <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-amber-900">
          <StickyNote className="h-3.5 w-3.5" /> Nota para el equipo (el cliente no la ve)
          {texto.length > 3500 ? (
            <span className={`ml-auto ${pasadoDeLargo ? "font-semibold text-red-600" : "text-amber-800"}`}>
              {texto.length}/{MAX_NOTA}
            </span>
          ) : null}
        </p>
        <textarea
          ref={area}
          rows={1}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              guardar();
            } else if (e.key === "Escape") {
              e.preventDefault();
              if (onCancelar) onCancelar();
            }
          }}
          placeholder="Escribí una nota solo para el equipo…"
          className="min-h-[40px] w-full resize-none rounded-lg border border-amber-300 bg-[#fffbe0] px-3 py-2.5 text-base leading-5 text-slate-900 shadow-sm lg:text-sm"
          data-testid="wa-nota-texto"
        />
      </div>
      <button
        type="button"
        onClick={onCancelar}
        title="Cancelar la nota"
        aria-label="Cancelar la nota"
        className="mb-0.5 shrink-0 rounded-full p-2 text-amber-900 hover:bg-black/5"
        data-testid="wa-nota-cancelar"
      >
        <X className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={guardar}
        disabled={guardando || !texto.trim() || pasadoDeLargo}
        className="flex h-10 shrink-0 items-center justify-center rounded-full bg-amber-500 px-3 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-50"
        data-testid="wa-nota-guardar"
      >
        Guardar nota
      </button>
    </div>
  );
}

// La burbuja de una nota en el hilo: amarilla y centrada, con borrar para quien corresponde.
export function NotaEquipo({ nota, onBorrada }) {
  const { user, isAdmin } = useAuth();
  const [borrando, setBorrando] = useState(false);
  const [oculta, setOculta] = useState(false);
  if (oculta || !nota || nota.borrada) return null;
  const puedeBorrar = Boolean(isAdmin) || Boolean(user && nota.usuario_id && nota.usuario_id === user.id);
  const hora = horaDe(nota.created_at);

  const borrar = async () => {
    setBorrando(true);
    try {
      await api.delete(`/whatsapp/mensajes/${encodeURIComponent(nota.id)}`);
      setOculta(true);
      if (onBorrada) onBorrada(nota.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBorrando(false);
    }
  };

  return (
    <div className="my-2 flex justify-center px-2" data-testid={`wa-nota-${nota.id}`}>
      <div className="max-w-[85%] rounded-lg border border-amber-200 bg-[#fff3b0] px-3 py-2 text-sm text-slate-900 shadow-sm">
        <p className="mb-0.5 flex items-center gap-1.5 text-[11px] font-medium text-amber-900">
          <StickyNote className="h-3 w-3 shrink-0" />
          <span>Nota de {nota.usuario || "alguien del equipo"} · solo la ve el equipo</span>
          {puedeBorrar ? (
            <button
              type="button"
              onClick={borrar}
              disabled={borrando}
              title="Borrar la nota"
              aria-label="Borrar la nota"
              className="ml-1 rounded p-0.5 text-amber-800 hover:bg-amber-200/60 disabled:opacity-50"
              data-testid={`wa-nota-borrar-${nota.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </p>
        <p className="whitespace-pre-wrap break-words">{nota.texto}</p>
        {hora ? <p className="mt-0.5 text-right text-[10px] text-amber-800/80">{hora}</p> : null}
      </div>
    </div>
  );
}
